import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { createSlice } from '@reduxjs/toolkit';
import {
  acceptFulfillmentOrder,
  assignDevicesToOrderVirtualDevices,
  cancelFulfillmentOrderDetails,
  deleteDeviceDetails,
  deleteFulfillmentOrderAttachment,
  deleteInventoryOrderAttachment,
  deleteVirtualDevice,
  downloadInventoryAttachment,
  factoryResetInventoryATA,
  getAtaConfigurationsList,
  getAtaPortConfigurationsList,
  getDevicesList,
  getFulfillmentOrderDetails,
  getFulfillmentOrderLogs,
  getFulfillmentOrdersList,
  getInventoryDeviceDetails,
  getInventoryList,
  getOrderLogs,
  getOrganizationModelsBundle,
  getVirtualDeviceDetails,
  moveToInventory,
  patchDevicesForSaleFlag,
  patchFulfillmentOrderDetailsApprove,
  patchFulfillmentOrderDetailsAssign,
  patchFulfillmentOrderDetailsConfirmDelivery,
  patchFulfillmentOrderUpdateInvoiceStatus,
  patchFulfillmentOrderUpdatePaymentStatus,
  patchImportDevicesToInventory,
  patchInventoryATAsFirmwares,
  patchOrganizationModelsBundle,
  patchVirtualAtaAcs,
  patchVirtualAtaAcsPort,
  patchVirtualDialer,
  patchVirtualPowerCycle,
  patchVirtualSim,
  postAddATAsToInventory,
  postAddBatteriesToInventory,
  postAddDialersToInventory,
  postAddEnclosuresToInventory,
  postAddPowerCyclesToInventory,
  postAddRoutersToInventory,
  postAddSIMsToInventory,
  postAddNonSerializedItemsToInventory,
  getNonSerializedItemsByWarehouse,
  getNonSerializedItemLogs,
  getNonSerializedItemsReport,
  postFulfillmentOrderDetails,
  postImportDevicesToEquipmentGroup,
  postOrderComment,
  postVirtualDevice,
  sendInventoryATACommand,
  unassignDevicesToOrderVirtualDevices,
  updateFulfillmentOrderWarehouse,
  getAccessoryBundles,
  postAccessoryBundle,
  patchAccessoryBundle,
  deleteAccessoryBundle,
  patchDeviceDetails,
  patchATAFirmware,
  postInventoryDeviceOwnershipTransfer,
  startDeviceQAcheck,
  finishDeviceQAcheck,
  postReplaceInventoryDevice,
  postAddSmartphonesToInventory,
} from './inventory.action';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  DeviceType,
  FulfillmentOrderLogType,
  FulfillmentOrderType,
  InventoryDeviceDetailsType,
  InventoryListItem,
  OrderLogType,
  OrganizationModelBundleType,
  NonSerializedItemType,
  NonSerializedItemLogType,
  AccessoryBundleType,
} from '../../types/inventory/inventory.types';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import { ACSATAConfigType } from '../../types/ata/acs-ata/acs-ata-config/acs-ata-config.type';
import { AcsAtaVirtualDeviceType, DeviceCategory } from '../../types/order/order.type';
import {
  assignDevicesToPackage,
  createShippingPackage,
  deleteShippingPackage,
  getShippingPackage,
  getShippingPackages,
  updateShippingPackage,
} from './shipping-package.action';
import { ShippingPackageType } from '../../types/inventory/shipping-package.types';

type DeviceRouteParams = RouteParamsType & {
  category: DeviceCategory;
  warehouse_id: string;
  model_id: string;
  status: string;
  synchronized?: string;
  connected?: string;
  sim_state?: string;
};

export interface InventoryState {
  virtualDeviceDetails: null | AcsAtaVirtualDeviceType;
  virtualDeviceDetailsFetchStatus: FETCH_STATUS;
  devicesAvailableForSalePatchStatus: FETCH_STATUS;
  inventoryDeviceDetails: null | InventoryDeviceDetailsType;
  inventoryDeviceDetailsFetchStatus: FETCH_STATUS;
  importDevicesToEquipmentGroupPostStatus: FETCH_STATUS;
  transferOwnershipPostStatus: FETCH_STATUS;
  importDevicesToWarehousePatchStatus: FETCH_STATUS;
  replaceInventoryDevicePostStatus: FETCH_STATUS;
  patchInventoryATAsFirmwaresPatchStatus: FETCH_STATUS;
  addATAToInventoryPostStatus: FETCH_STATUS;
  addBatteryToInventoryPostStatus: FETCH_STATUS;
  addSIMToInventoryPostStatus: FETCH_STATUS;
  addSmartphoneInventoryPostStatus: FETCH_STATUS;
  addDialerToInventoryPostStatus: FETCH_STATUS;
  addRouterToInventoryPostStatus: FETCH_STATUS;
  addEnclosureToInventoryPostStatus: FETCH_STATUS;
  addPowerCycleToInventoryPostStatus: FETCH_STATUS;
  addNonSerializedItemsToInventoryPostStatus: FETCH_STATUS;
  nonSerializedItemsList: Array<NonSerializedItemType> | null;
  nonSerializedItemsListFetchStatus: FETCH_STATUS;
  nonSerializedItemLogs: Array<NonSerializedItemLogType> | null;
  nonSerializedItemLogsFetchStatus: FETCH_STATUS;
  orderCommentPostStatus: FETCH_STATUS;
  inventoryList: null | {
    atas: Array<InventoryListItem>;
    batteries: Array<InventoryListItem>;
    dialers: Array<InventoryListItem>;
    enclosures: Array<InventoryListItem>;
    powerCycles: Array<InventoryListItem>;
    routers: Array<InventoryListItem>;
    sims: Array<InventoryListItem>;
  };
  inventoryListFetchStatus: FETCH_STATUS;
  devicesList: null | PaginationType<DeviceType>;
  devicesListFetchStatus: FETCH_STATUS;
  devicesRouteParams: DeviceRouteParams;
  deviceDetailsDeleteStatus: FETCH_STATUS;
  moveToInventoryPatchStatus: FETCH_STATUS;
  fulfillmentOrdersList: null | PaginationType<FulfillmentOrderType>;
  fulfillmentOrdersListFetchStatus: FETCH_STATUS;
  fulfillmentOrdersRouteParams: RouteParamsType;
  fulfillmentOrderDetailsDeleteStatus: FETCH_STATUS;
  fulfillmentOrderDetailsCancelStatus: FETCH_STATUS;
  fulfillmentOrderDetailsPostStatus: FETCH_STATUS;
  fulfillmentOrderDetailsCreated: null | FulfillmentOrderType;
  virtualDevicePostStatus: FETCH_STATUS;
  virtualDeviceDeleteStatus: FETCH_STATUS;
  assignDevicesToOrderVirtualDevicesPostStatus: FETCH_STATUS;
  unassignDevicesToOrderVirtualDevicesPostStatus: FETCH_STATUS;
  fulfillmentOrderDetailsApprovePatchStatus: FETCH_STATUS;
  fulfillmentOrderDetailsAssignPatchStatus: FETCH_STATUS;
  fulfillmentOrderDetailsFetchStatus: FETCH_STATUS;
  fulfillmentOrderDetails: null | FulfillmentOrderType;
  fulfillmentOrderDetailsConfirmDeliveryPatchStatus: FETCH_STATUS;
  fulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus: FETCH_STATUS;
  fulfillmentOrderDetailsUpdatePaymentStatusPatchStatus: FETCH_STATUS;
  fulfillmentOrderDetailsPatchStatus: FETCH_STATUS;
  organizationModulesBundle: null | OrganizationModelBundleType;
  organizationModulesBundleFetchStatus: FETCH_STATUS;
  organizationModulesBundlePatchStatus: FETCH_STATUS;
  acsAtaConfigurationsList: null | Array<ACSATAConfigType>;
  acsAtaConfigurationsListFetchStatus: FETCH_STATUS;
  acsAtaPortConfigurationsList: null | Array<ACSATAConfigType>;
  acsAtaPortConfigurationsListFetchStatus: FETCH_STATUS;
  virtualPowerCyclePatchStatus: FETCH_STATUS;
  virtualDialerPatchStatus: FETCH_STATUS;
  virtualSimPatchStatus: FETCH_STATUS;
  virtualAtaAcsPatchStatus: FETCH_STATUS;
  virtualAtaAcsPatchStatusPort: FETCH_STATUS;
  fulfillmentOrderLogs: FulfillmentOrderLogType[];
  fulfillmentOrderLogsFetchStatus: FETCH_STATUS;
  orderLogs: OrderLogType[];
  orderLogsFetchStatus: FETCH_STATUS;
  shippingPackages: ShippingPackageType[];
  shippingPackagesFetchStatus: FETCH_STATUS;
  shippingPackagesError: string | null;
  sendInventoryATACommandPostStatus: FETCH_STATUS;
  factoryResetInventoryATAPostStatus: FETCH_STATUS;

  currentShippingPackage: ShippingPackageType | null;
  currentShippingPackageFetchStatus: FETCH_STATUS;
  currentShippingPackageError: string | null;

  createShippingPackageStatus: FETCH_STATUS;
  createShippingPackageError: string | null;
  createShippingPackage: null | { index: number; id: number };

  updateShippingPackageStatus: FETCH_STATUS;
  updateShippingPackageError: string | null;

  deleteShippingPackageStatus: FETCH_STATUS;
  deleteShippingPackageError: string | null;

  deviceDetailsPatchStatus: FETCH_STATUS;
  fulfillmentOrderAttachmentDeleteStatus: FETCH_STATUS;
  accessoryBundles: AccessoryBundleType[] | null;
  accessoryBundlesFetchStatus: FETCH_STATUS;
  accessoryBundleSaveStatus: FETCH_STATUS;
}

export const initialDevicesRouteParams: DeviceRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.id',
  q: '',
  category: DeviceCategory.Ata,
  warehouse_id: '',
  status: '',
  model_id: '',
  synchronized: 'all',
  connected: 'all',
  sim_state: '',
};

export const initialFulfillmentOrdersRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.id',
  q: '',
  status: '',
  from_warehouse_id: '',
  to_warehouse_id: '',
};

const INIT_STATE: InventoryState = {
  transferOwnershipPostStatus: FETCH_STATUS.NULL,
  importDevicesToEquipmentGroupPostStatus: FETCH_STATUS.NULL,
  importDevicesToWarehousePatchStatus: FETCH_STATUS.NULL,
  replaceInventoryDevicePostStatus: FETCH_STATUS.NULL,
  virtualDeviceDetails: null,
  virtualDeviceDetailsFetchStatus: FETCH_STATUS.NULL,
  devicesAvailableForSalePatchStatus: FETCH_STATUS.NULL,
  patchInventoryATAsFirmwaresPatchStatus: FETCH_STATUS.NULL,
  inventoryDeviceDetails: null,
  inventoryDeviceDetailsFetchStatus: FETCH_STATUS.NULL,
  addATAToInventoryPostStatus: FETCH_STATUS.NULL,
  addBatteryToInventoryPostStatus: FETCH_STATUS.NULL,
  addSIMToInventoryPostStatus: FETCH_STATUS.NULL,
  addSmartphoneInventoryPostStatus: FETCH_STATUS.NULL,
  addDialerToInventoryPostStatus: FETCH_STATUS.NULL,
  addRouterToInventoryPostStatus: FETCH_STATUS.NULL,
  addEnclosureToInventoryPostStatus: FETCH_STATUS.NULL,
  addNonSerializedItemsToInventoryPostStatus: FETCH_STATUS.NULL,
  nonSerializedItemsList: null,
  nonSerializedItemsListFetchStatus: FETCH_STATUS.NULL,
  nonSerializedItemLogs: null,
  nonSerializedItemLogsFetchStatus: FETCH_STATUS.NULL,
  orderCommentPostStatus: FETCH_STATUS.NULL,
  addPowerCycleToInventoryPostStatus: FETCH_STATUS.NULL,
  assignDevicesToOrderVirtualDevicesPostStatus: FETCH_STATUS.NULL,
  unassignDevicesToOrderVirtualDevicesPostStatus: FETCH_STATUS.NULL,
  inventoryList: null,
  inventoryListFetchStatus: FETCH_STATUS.NULL,
  devicesList: null,
  devicesListFetchStatus: FETCH_STATUS.NULL,
  devicesRouteParams: initialDevicesRouteParams,
  deviceDetailsDeleteStatus: FETCH_STATUS.NULL,
  moveToInventoryPatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrdersList: null,
  fulfillmentOrdersListFetchStatus: FETCH_STATUS.NULL,
  fulfillmentOrdersRouteParams: initialFulfillmentOrdersRouteParams,
  fulfillmentOrderDetailsDeleteStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsCancelStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsPostStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsCreated: null,
  virtualPowerCyclePatchStatus: FETCH_STATUS.NULL,
  virtualDialerPatchStatus: FETCH_STATUS.NULL,
  virtualSimPatchStatus: FETCH_STATUS.NULL,
  virtualDevicePostStatus: FETCH_STATUS.NULL,
  virtualDeviceDeleteStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsApprovePatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsAssignPatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsFetchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetails: null,
  fulfillmentOrderDetailsConfirmDeliveryPatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsUpdatePaymentStatusPatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderDetailsPatchStatus: FETCH_STATUS.NULL,
  organizationModulesBundleFetchStatus: FETCH_STATUS.NULL,
  organizationModulesBundlePatchStatus: FETCH_STATUS.NULL,
  organizationModulesBundle: null,
  acsAtaConfigurationsListFetchStatus: FETCH_STATUS.NULL,
  acsAtaConfigurationsList: null,
  acsAtaPortConfigurationsListFetchStatus: FETCH_STATUS.NULL,
  acsAtaPortConfigurationsList: null,
  virtualAtaAcsPatchStatus: FETCH_STATUS.NULL,
  virtualAtaAcsPatchStatusPort: FETCH_STATUS.NULL,
  fulfillmentOrderLogs: [] as FulfillmentOrderLogType[],
  fulfillmentOrderLogsFetchStatus: FETCH_STATUS.NULL,
  orderLogs: [] as OrderLogType[],
  orderLogsFetchStatus: FETCH_STATUS.NULL,
  shippingPackages: [],
  shippingPackagesFetchStatus: FETCH_STATUS.NULL,
  shippingPackagesError: null,

  currentShippingPackage: null,
  currentShippingPackageFetchStatus: FETCH_STATUS.NULL,
  currentShippingPackageError: null,

  createShippingPackageStatus: FETCH_STATUS.NULL,
  createShippingPackageError: null,
  createShippingPackage: null,

  updateShippingPackageStatus: FETCH_STATUS.NULL,
  updateShippingPackageError: null,

  deleteShippingPackageStatus: FETCH_STATUS.NULL,
  deleteShippingPackageError: null,

  deviceDetailsPatchStatus: FETCH_STATUS.NULL,
  fulfillmentOrderAttachmentDeleteStatus: FETCH_STATUS.NULL,
  sendInventoryATACommandPostStatus: FETCH_STATUS.NULL,
  factoryResetInventoryATAPostStatus: FETCH_STATUS.NULL,

  accessoryBundles: null,
  accessoryBundlesFetchStatus: FETCH_STATUS.NULL,
  accessoryBundleSaveStatus: FETCH_STATUS.NULL,
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState: INIT_STATE,
  reducers: {
    setAddATAToInventoryPostStatus: (state, action) => {
      state.addATAToInventoryPostStatus = action.payload;
    },
    setDevicesAvailableForSalePatchStatus: (state, action) => {
      state.devicesAvailableForSalePatchStatus = action.payload;
    },
    setAddBatteryToInventoryPostStatus: (state, action) => {
      state.addBatteryToInventoryPostStatus = action.payload;
    },
    setAddSIMToInventoryPostStatus: (state, action) => {
      state.addSIMToInventoryPostStatus = action.payload;
    },
    setAddSmartphoneToInventoryPostStatus: (state, action) => {
      state.addSmartphoneInventoryPostStatus = action.payload;
    },
    setAddDialerToInventoryPostStatus: (state, action) => {
      state.addDialerToInventoryPostStatus = action.payload;
    },
    setAddRouterToInventoryPostStatus: (state, action) => {
      state.addRouterToInventoryPostStatus = action.payload;
    },
    setTransferOwnershipPostStatus: (state, action) => {
      state.transferOwnershipPostStatus = action.payload;
    },
    setAddEnclosureToInventoryPostStatus: (state, action) => {
      state.addEnclosureToInventoryPostStatus = action.payload;
    },
    setAddPowerCycleToInventoryPostStatus: (state, action) => {
      state.addPowerCycleToInventoryPostStatus = action.payload;
    },
    setAddNonSerializedItemsToInventoryPostStatus: (state, action) => {
      state.addNonSerializedItemsToInventoryPostStatus = action.payload;
    },
    setAccessoryBundleSaveStatus: (state, action) => {
      state.accessoryBundleSaveStatus = action.payload;
    },
    setNonSerializedItemsList: (state, action) => {
      state.nonSerializedItemsList = action.payload;
    },
    setNonSerializedItemsListFetchStatus: (state, action) => {
      state.nonSerializedItemsListFetchStatus = action.payload;
    },
    setInventoryList: (state, action) => {
      state.inventoryList = action.payload;
    },
    setInventoryListFetchStatus: (state, action) => {
      state.inventoryListFetchStatus = action.payload;
    },
    setDevicesList: (state, action) => {
      state.devicesList = action.payload;
    },
    setDevicesListFetchStatus: (state, action) => {
      state.devicesListFetchStatus = action.payload;
    },
    setDevicesRouteParams: (state, action) => {
      state.devicesRouteParams = { ...state.devicesRouteParams, ...action.payload };
    },
    setFulfillmentOrdersList: (state, action) => {
      state.fulfillmentOrdersList = action.payload;
    },
    setFulfillmentOrdersListFetchStatus: (state, action) => {
      state.fulfillmentOrdersListFetchStatus = action.payload;
    },
    setDeviceDetailsDeleteStatus: (state, action) => {
      state.deviceDetailsDeleteStatus = action.payload;
    },
    setMoveToInventoryPatchStatus: (state, action) => {
      state.moveToInventoryPatchStatus = action.payload;
    },
    setFulfillmentOrderDetailsDeleteStatus: (state, action) => {
      state.fulfillmentOrderDetailsDeleteStatus = action.payload;
    },
    setFulfillmentOrderDetailsCancelStatus: (state, action) => {
      state.fulfillmentOrderDetailsCancelStatus = action.payload;
    },
    setFulfillmentOrderDetailsPostStatus: (state, action) => {
      state.fulfillmentOrderDetailsPostStatus = action.payload;
    },
    setFulfillmentOrderDetailsCreated: (state, action) => {
      state.fulfillmentOrderDetailsCreated = action.payload;
    },
    setVirtualDevicePostStatus: (state, action) => {
      state.virtualDevicePostStatus = action.payload;
    },
    setVirtualDeviceDeleteStatus: (state, action) => {
      state.virtualDeviceDeleteStatus = action.payload;
    },
    setFulfillmentOrderDetails: (state, action) => {
      state.fulfillmentOrderDetails = action.payload;
    },
    setFulfillmentOrderDetailsFetchStatus: (state, action) => {
      state.fulfillmentOrderDetailsFetchStatus = action.payload;
    },
    setFulfillmentOrderDetailsApprovePatchStatus: (state, action) => {
      state.fulfillmentOrderDetailsApprovePatchStatus = action.payload;
    },
    setFulfillmentOrderDetailsAssignPatchStatus: (state, action) => {
      state.fulfillmentOrderDetailsAssignPatchStatus = action.payload;
    },
    setFulfillmentOrderDetailsConfirmDeliveryPatchStatus: (state, action) => {
      state.fulfillmentOrderDetailsConfirmDeliveryPatchStatus = action.payload;
    },
    setOrganizationModulesBundlePatchStatus: (state, action) => {
      state.organizationModulesBundlePatchStatus = action.payload;
    },
    setOrganizationModulesBundleFetchStatus: (state, action) => {
      state.organizationModulesBundleFetchStatus = action.payload;
    },
    setOrganizationModulesBundle: (state, action) => {
      state.organizationModulesBundle = action.payload;
    },
    setAssignDevicesToOrderVirtualDevicesPostStatus: (state, action) => {
      state.assignDevicesToOrderVirtualDevicesPostStatus = action.payload;
    },
    setUnassignDevicesToOrderVirtualDevicesPostStatus: (state, action) => {
      state.unassignDevicesToOrderVirtualDevicesPostStatus = action.payload;
    },
    setOrderCommentPostStatus: (state, action) => {
      state.orderCommentPostStatus = action.payload;
    },
    setAcsAtaConfigurationsListFetchStatus: (state, action) => {
      state.acsAtaConfigurationsListFetchStatus = action.payload;
    },
    setAcsAtaConfigurationsList: (state, action) => {
      state.acsAtaConfigurationsList = action.payload;
    },
    setAcsAtaPortConfigurationsListFetchStatus: (state, action) => {
      state.acsAtaPortConfigurationsListFetchStatus = action.payload;
    },
    setAcsAtaPortConfigurationsList: (state, action) => {
      state.acsAtaPortConfigurationsList = action.payload;
    },
    setVirtualDeviceDetails: (state, action) => {
      state.virtualDeviceDetails = action.payload;
    },
    setVirtualDeviceDetailsFetchStatus: (state, action) => {
      state.virtualDeviceDetailsFetchStatus = action.payload;
    },
    setInventoryDeviceDetails: (state, action) => {
      state.inventoryDeviceDetails = action.payload;
    },
    setInventoryDeviceDetailsFetchStatus: (state, action) => {
      state.inventoryDeviceDetailsFetchStatus = action.payload;
    },
    setVirtualPowerCyclePatchStatus: (state, action) => {
      state.virtualPowerCyclePatchStatus = action.payload;
    },
    setVirtualSimPatchStatus: (state, action) => {
      state.virtualSimPatchStatus = action.payload;
    },
    setVirtualDialerPatchStatus: (state, action) => {
      state.virtualDialerPatchStatus = action.payload;
    },
    setVirtualAtaAcsPatchStatus: (state, action) => {
      state.virtualAtaAcsPatchStatus = action.payload;
    },
    setVirtualAtaAcsPatchStatusPort: (state, action) => {
      state.virtualAtaAcsPatchStatusPort = action.payload;
    },
    setDeleteShippingPackageStatus: (state, action) => {
      state.deleteShippingPackageStatus = action.payload;
    },
    setImportDevicesToEquipmentGroupPostStatus: (state, action) => {
      state.importDevicesToEquipmentGroupPostStatus = action.payload;
    },
    setImportDevicesToWarehousePatchStatus: (state, action) => {
      state.importDevicesToWarehousePatchStatus = action.payload;
    },
    setReplaceInventoryDevicePostStatus: (state, action) => {
      state.replaceInventoryDevicePostStatus = action.payload;
    },
    setPatchInventoryATAsFirmwaresPatchStatus: (state, action) => {
      state.patchInventoryATAsFirmwaresPatchStatus = action.payload;
    },
    resetShippingPackageState: state => {
      state.shippingPackages = [];
      state.shippingPackagesFetchStatus = FETCH_STATUS.NULL;
      state.shippingPackagesError = null;

      state.currentShippingPackage = null;
      state.currentShippingPackageFetchStatus = FETCH_STATUS.NULL;
      state.currentShippingPackageError = null;

      state.createShippingPackageStatus = FETCH_STATUS.NULL;
      state.createShippingPackageError = null;

      state.updateShippingPackageStatus = FETCH_STATUS.NULL;
      state.updateShippingPackageError = null;

      state.deleteShippingPackageStatus = FETCH_STATUS.NULL;
      state.deleteShippingPackageError = null;
    },
    setFulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus: (state, action) => {
      state.fulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus = action.payload;
    },
    setFulfillmentOrderDetailsUpdatePaymentStatusPatchStatus: (state, action) => {
      state.fulfillmentOrderDetailsUpdatePaymentStatusPatchStatus = action.payload;
    },
    setDeviceDetailsPatchStatus: (state, action) => {
      state.deviceDetailsPatchStatus = action.payload;
    },
    setFulfillmentOrderAttachmentDeleteStatus: (state, action) => {
      state.fulfillmentOrderAttachmentDeleteStatus = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'inventory-devices') {
        state.devicesRouteParams = { ...state.devicesRouteParams, ...payload.data };
      }

      if (payload.reducer === 'inventory-fulfillment-orders') {
        state.fulfillmentOrdersRouteParams = { ...state.fulfillmentOrdersRouteParams, ...payload.data };
      }
    });

    // POST ADD ATA TO INVENTORY
    builder.addCase(postAddATAsToInventory.pending, state => {
      state.addATAToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddATAsToInventory.rejected, (state, action) => {
      state.addATAToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddATAsToInventory.fulfilled, state => {
      state.addATAToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD BATTERY TO INVENTORY
    builder.addCase(postAddBatteriesToInventory.pending, state => {
      state.addBatteryToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddBatteriesToInventory.rejected, (state, action) => {
      state.addBatteryToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddBatteriesToInventory.fulfilled, state => {
      state.addBatteryToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD SIM TO INVENTORY
    builder.addCase(postAddSIMsToInventory.pending, state => {
      state.addSIMToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddSIMsToInventory.rejected, (state, action) => {
      state.addSIMToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddSIMsToInventory.fulfilled, state => {
      state.addSIMToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD SMARTPHONE TO INVENTORY
    builder.addCase(postAddSmartphonesToInventory.pending, state => {
      state.addSmartphoneInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddSmartphonesToInventory.rejected, (state, action) => {
      state.addSmartphoneInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddSmartphonesToInventory.fulfilled, state => {
      state.addSmartphoneInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD DIALER TO INVENTORY
    builder.addCase(postAddDialersToInventory.pending, state => {
      state.addDialerToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddDialersToInventory.rejected, (state, action) => {
      state.addDialerToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddDialersToInventory.fulfilled, state => {
      state.addDialerToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD ROUTER TO INVENTORY
    builder.addCase(postAddRoutersToInventory.pending, state => {
      state.addRouterToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddRoutersToInventory.rejected, (state, action) => {
      state.addRouterToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddRoutersToInventory.fulfilled, state => {
      state.addRouterToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD ENCLOSURE TO INVENTORY
    builder.addCase(postAddEnclosuresToInventory.pending, state => {
      state.addEnclosureToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddEnclosuresToInventory.rejected, (state, action) => {
      state.addEnclosureToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddEnclosuresToInventory.fulfilled, state => {
      state.addEnclosureToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD POWERCYCLES TO INVENTORY
    builder.addCase(postAddPowerCyclesToInventory.pending, state => {
      state.addPowerCycleToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddPowerCyclesToInventory.rejected, (state, action) => {
      state.addPowerCycleToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddPowerCyclesToInventory.fulfilled, state => {
      state.addPowerCycleToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ADD NON-SERIALIZED ITEMS TO INVENTORY
    builder.addCase(postAddNonSerializedItemsToInventory.pending, state => {
      state.addNonSerializedItemsToInventoryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAddNonSerializedItemsToInventory.rejected, (state, action) => {
      state.addNonSerializedItemsToInventoryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postAddNonSerializedItemsToInventory.fulfilled, state => {
      state.addNonSerializedItemsToInventoryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // GET NON-SERIALIZED ITEMS BY WAREHOUSE
    builder.addCase(getNonSerializedItemsByWarehouse.pending, state => {
      state.nonSerializedItemsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getNonSerializedItemsByWarehouse.rejected, (state, action) => {
      state.nonSerializedItemsListFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(getNonSerializedItemsByWarehouse.fulfilled, (state, action) => {
      state.nonSerializedItemsList = action.payload;
      state.nonSerializedItemsListFetchStatus = FETCH_STATUS.FULFILLED;
    });

    // GET NON-SERIALIZED ITEMS REPORT (ALL WAREHOUSES)
    builder.addCase(getNonSerializedItemsReport.pending, state => {
      state.nonSerializedItemsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getNonSerializedItemsReport.rejected, (state, action) => {
      state.nonSerializedItemsListFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(getNonSerializedItemsReport.fulfilled, (state, action) => {
      state.nonSerializedItemsList = action.payload;
      state.nonSerializedItemsListFetchStatus = FETCH_STATUS.FULFILLED;
    });

    // GET NON-SERIALIZED ITEM LOGS
    builder.addCase(getNonSerializedItemLogs.pending, state => {
      state.nonSerializedItemLogsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getNonSerializedItemLogs.rejected, (state, action) => {
      state.nonSerializedItemLogsFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(getNonSerializedItemLogs.fulfilled, (state, action) => {
      state.nonSerializedItemLogs = action.payload;
      state.nonSerializedItemLogsFetchStatus = FETCH_STATUS.FULFILLED;
    });

    // GET INVENTORY
    builder.addCase(getInventoryList.pending, state => {
      state.inventoryListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getInventoryList.rejected, (state, action) => {
      if (action.payload) {
        state.inventoryListFetchStatus = FETCH_STATUS.REJECTED;
        // state.ataBaseTemplatesList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getInventoryList.fulfilled, (state, action) => {
      state.inventoryListFetchStatus = FETCH_STATUS.FULFILLED;
      state.inventoryList = action.payload;
    });

    // GET INVENTORY
    builder.addCase(getDevicesList.pending, state => {
      state.devicesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDevicesList.rejected, (state, action) => {
      if (action.payload) {
        state.devicesListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getDevicesList.fulfilled, (state, action) => {
      state.devicesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.devicesList = action.payload;
    });

    // GET INVENTORY
    builder.addCase(getAtaConfigurationsList.pending, state => {
      state.acsAtaConfigurationsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getAtaConfigurationsList.rejected, (state, action) => {
      if (action.payload) {
        state.acsAtaConfigurationsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getAtaConfigurationsList.fulfilled, (state, action) => {
      state.acsAtaConfigurationsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.acsAtaConfigurationsList = action.payload;
    });

    // GET INVENTORY
    builder.addCase(getAtaPortConfigurationsList.pending, state => {
      state.acsAtaPortConfigurationsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getAtaPortConfigurationsList.rejected, (state, action) => {
      if (action.payload) {
        state.acsAtaPortConfigurationsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getAtaPortConfigurationsList.fulfilled, (state, action) => {
      state.acsAtaPortConfigurationsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.acsAtaPortConfigurationsList = action.payload;
    });

    // DELETE DEVICE DETAILS
    builder.addCase(deleteDeviceDetails.pending, state => {
      state.deviceDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteDeviceDetails.rejected, (state, action) => {
      state.deviceDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteDeviceDetails.fulfilled, state => {
      state.deviceDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Device deleted successfully!');
      });
    });

    // MOVE DEVICE DETAILS
    builder.addCase(moveToInventory.pending, state => {
      state.moveToInventoryPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(moveToInventory.rejected, (state, action) => {
      state.moveToInventoryPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(moveToInventory.fulfilled, state => {
      state.moveToInventoryPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Device moved successfully!');
      });
    });

    // GET ORDERS
    builder.addCase(getFulfillmentOrdersList.pending, state => {
      state.fulfillmentOrdersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getFulfillmentOrdersList.rejected, (state, action) => {
      if (action.payload) {
        state.fulfillmentOrdersListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getFulfillmentOrdersList.fulfilled, (state, action) => {
      state.fulfillmentOrdersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.fulfillmentOrdersList = action.payload;
    });

    // POST ADD ROUTER TO INVENTORY
    builder.addCase(postFulfillmentOrderDetails.pending, state => {
      state.fulfillmentOrderDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postFulfillmentOrderDetails.rejected, (state, action) => {
      state.fulfillmentOrderDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postFulfillmentOrderDetails.fulfilled, (state, action) => {
      state.fulfillmentOrderDetailsPostStatus = FETCH_STATUS.FULFILLED;
      state.fulfillmentOrderDetailsCreated = action.payload;
      setTimeout(() => snackbarUtils.success('The order was created successfully!'));
    });

    // POST VIRTUAL DEVICE
    builder.addCase(postVirtualDevice.pending, state => {
      state.virtualDevicePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postVirtualDevice.rejected, (state, action) => {
      state.virtualDevicePostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(postVirtualDevice.fulfilled, state => {
      state.virtualDevicePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('The device was added successfully!'));
    });

    // POST VIRTUAL DEVICE
    builder.addCase(deleteVirtualDevice.pending, state => {
      state.virtualDeviceDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteVirtualDevice.rejected, (state, action) => {
      state.virtualDeviceDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(deleteVirtualDevice.fulfilled, state => {
      state.virtualDeviceDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('The device was removed successfully!'));
    });

    // GET ORDER DETAILS
    builder.addCase(getFulfillmentOrderDetails.pending, state => {
      state.fulfillmentOrderDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getFulfillmentOrderDetails.rejected, (state, action) => {
      if (action.payload) {
        state.fulfillmentOrderDetailsFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getFulfillmentOrderDetails.fulfilled, (state, action) => {
      state.fulfillmentOrderDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.fulfillmentOrderDetails = action.payload;
    });

    // GET ORDER DETAILS
    builder.addCase(getVirtualDeviceDetails.pending, state => {
      state.virtualDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getVirtualDeviceDetails.rejected, (state, action) => {
      if (action.payload) {
        state.virtualDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getVirtualDeviceDetails.fulfilled, (state, action) => {
      state.virtualDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.virtualDeviceDetails = action.payload;
    });

    // PATCH DEVICE DETAILS
    builder.addCase(patchDevicesForSaleFlag.pending, state => {
      state.devicesAvailableForSalePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchDevicesForSaleFlag.rejected, (state, action) => {
      if (action.payload) {
        state.devicesAvailableForSalePatchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(patchDevicesForSaleFlag.fulfilled, state => {
      state.devicesAvailableForSalePatchStatus = FETCH_STATUS.FULFILLED;
    });

    // GET INVENTORY DEVICE DETAILS
    builder.addCase(getInventoryDeviceDetails.pending, state => {
      state.inventoryDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getInventoryDeviceDetails.rejected, (state, action) => {
      if (action.payload) {
        state.inventoryDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getInventoryDeviceDetails.fulfilled, (state, action) => {
      state.inventoryDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.inventoryDeviceDetails = action.payload;
    });

    // GET ORGANIZATION MODELS BUNDLE
    builder.addCase(getOrganizationModelsBundle.pending, state => {
      state.organizationModulesBundleFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getOrganizationModelsBundle.rejected, (state, action) => {
      if (action.payload) {
        state.organizationModulesBundleFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getOrganizationModelsBundle.fulfilled, (state, action) => {
      state.organizationModulesBundleFetchStatus = FETCH_STATUS.FULFILLED;
      state.organizationModulesBundle = action.payload;
    });

    // Accessory Bundles
    builder.addCase(getAccessoryBundles.pending, state => {
      state.accessoryBundlesFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getAccessoryBundles.fulfilled, (state, action) => {
      state.accessoryBundles = action.payload;
      state.accessoryBundlesFetchStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(getAccessoryBundles.rejected, (state, action) => {
      state.accessoryBundlesFetchStatus = FETCH_STATUS.REJECTED;
      if (action.payload) {
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });

    builder.addCase(postAccessoryBundle.pending, state => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAccessoryBundle.fulfilled, (state, action) => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.FULFILLED;
      const bundle = action.payload;
      if (!state.accessoryBundles) {
        state.accessoryBundles = [bundle];
      } else {
        state.accessoryBundles.push(bundle);
      }
    });
    builder.addCase(postAccessoryBundle.rejected, (state, action) => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.REJECTED;
      if (action.payload) {
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });

    builder.addCase(patchAccessoryBundle.pending, state => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchAccessoryBundle.fulfilled, (state, action) => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.FULFILLED;
      if (state.accessoryBundles) {
        const index = state.accessoryBundles.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.accessoryBundles[index] = action.payload;
        }
      }
    });
    builder.addCase(patchAccessoryBundle.rejected, (state, action) => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.REJECTED;
      if (action.payload) {
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });

    builder.addCase(deleteAccessoryBundle.pending, state => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteAccessoryBundle.fulfilled, (state, action) => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.FULFILLED;
      if (state.accessoryBundles) {
        const index = state.accessoryBundles.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.accessoryBundles[index] = action.payload;
        }
      }
    });
    builder.addCase(deleteAccessoryBundle.rejected, (state, action) => {
      state.accessoryBundleSaveStatus = FETCH_STATUS.REJECTED;
      if (action.payload) {
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });

    // PATCH APPROVE
    builder.addCase(patchFulfillmentOrderDetailsApprove.pending, state => {
      state.fulfillmentOrderDetailsApprovePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchFulfillmentOrderDetailsApprove.rejected, (state, action) => {
      state.fulfillmentOrderDetailsApprovePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchFulfillmentOrderDetailsApprove.fulfilled, state => {
      state.fulfillmentOrderDetailsApprovePatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('The order was approved successfully!'));
    });

    // PATCH ASSIGN
    builder.addCase(patchFulfillmentOrderDetailsAssign.pending, state => {
      state.fulfillmentOrderDetailsAssignPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchFulfillmentOrderDetailsAssign.rejected, (state, action) => {
      state.fulfillmentOrderDetailsAssignPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchFulfillmentOrderDetailsAssign.fulfilled, state => {
      state.fulfillmentOrderDetailsAssignPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Devices have been assigned!'));
    });

    // PATCH CONFIRM DETAILS
    builder.addCase(patchFulfillmentOrderDetailsConfirmDelivery.pending, state => {
      state.fulfillmentOrderDetailsConfirmDeliveryPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchFulfillmentOrderDetailsConfirmDelivery.rejected, (state, action) => {
      state.fulfillmentOrderDetailsConfirmDeliveryPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchFulfillmentOrderDetailsConfirmDelivery.fulfilled, state => {
      state.fulfillmentOrderDetailsConfirmDeliveryPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('The order was updated successfully!'));
    });

    // PATCH ORGANIZATION MODELS BUNDLE
    builder.addCase(patchOrganizationModelsBundle.pending, state => {
      state.organizationModulesBundlePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchOrganizationModelsBundle.rejected, (state, action) => {
      state.organizationModulesBundlePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchOrganizationModelsBundle.fulfilled, state => {
      state.organizationModulesBundlePatchStatus = FETCH_STATUS.FULFILLED;
      state.organizationModulesBundle = null;
      state.organizationModulesBundleFetchStatus = FETCH_STATUS.NULL;
      setTimeout(() => snackbarUtils.success('Models updated successfully!'));
    });

    // PATCH ASSIGNING DEVICE
    builder.addCase(assignDevicesToOrderVirtualDevices.pending, state => {
      state.assignDevicesToOrderVirtualDevicesPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(assignDevicesToOrderVirtualDevices.rejected, (state, action) => {
      state.assignDevicesToOrderVirtualDevicesPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(assignDevicesToOrderVirtualDevices.fulfilled, state => {
      state.assignDevicesToOrderVirtualDevicesPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device have been successfully assigned!'));
    });

    // PATCH ASSIGNING DEVICE
    builder.addCase(unassignDevicesToOrderVirtualDevices.pending, state => {
      state.unassignDevicesToOrderVirtualDevicesPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(unassignDevicesToOrderVirtualDevices.rejected, (state, action) => {
      state.unassignDevicesToOrderVirtualDevicesPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(unassignDevicesToOrderVirtualDevices.fulfilled, state => {
      state.unassignDevicesToOrderVirtualDevicesPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device have been successfully unassigned!'));
    });

    // PATCH VIRTUAL POWERCYCLE
    builder.addCase(patchVirtualPowerCycle.pending, state => {
      state.virtualPowerCyclePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchVirtualPowerCycle.rejected, (state, action) => {
      state.virtualPowerCyclePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchVirtualPowerCycle.fulfilled, state => {
      state.virtualPowerCyclePatchStatus = FETCH_STATUS.FULFILLED;
    });

    // PATCH VIRTUAL DIALER
    builder.addCase(patchVirtualDialer.pending, state => {
      state.virtualDialerPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchVirtualDialer.rejected, (state, action) => {
      state.virtualDialerPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchVirtualDialer.fulfilled, state => {
      state.virtualDialerPatchStatus = FETCH_STATUS.FULFILLED;
    });

    // PATCH VIRTUAL SIM
    builder.addCase(patchVirtualSim.pending, state => {
      state.virtualSimPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchVirtualSim.rejected, (state, action) => {
      state.virtualSimPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchVirtualSim.fulfilled, state => {
      state.virtualSimPatchStatus = FETCH_STATUS.FULFILLED;
    });

    // PATCH ASSIGNING DEVICE
    builder.addCase(patchVirtualAtaAcs.pending, state => {
      state.virtualAtaAcsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchVirtualAtaAcs.rejected, (state, action) => {
      state.virtualAtaAcsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchVirtualAtaAcs.fulfilled, (state, action) => {
      const virtualAtaAcs = { ...state.virtualDeviceDetails };
      if (action.meta?.arg?.dto?.configOverrides) {
        state.virtualDeviceDetails = {
          ...(virtualAtaAcs as AcsAtaVirtualDeviceType),
          configOverrides: action.meta.arg.dto.configOverrides,
        };
      }
      state.virtualAtaAcsPatchStatus = FETCH_STATUS.FULFILLED;
    });

    // POST COMMENT ORDER DETAILS
    builder.addCase(postOrderComment.pending, state => {
      state.orderCommentPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postOrderComment.rejected, (state, action) => {
      state.orderCommentPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postOrderComment.fulfilled, (state, action) => {
      state.orderCommentPostStatus = FETCH_STATUS.FULFILLED;
      if (state.fulfillmentOrderDetails) {
        const tmp = { ...state.fulfillmentOrderDetails };
        state.fulfillmentOrderDetails = {
          ...tmp,
          comments: [
            {
              ...action.payload,
              createdAt: new Date(+Date.now() - 1000).toISOString(),
            },
            ...(tmp?.comments || []),
          ],
        };
      }
      setTimeout(() => {
        snackbarUtils.success('Comment created successfully!');
      });
    });

    // PATCH ASSIGNING DEVICE
    builder.addCase(patchVirtualAtaAcsPort.pending, state => {
      state.virtualAtaAcsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchVirtualAtaAcsPort.rejected, (state, action) => {
      state.virtualAtaAcsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchVirtualAtaAcsPort.fulfilled, (state, action) => {
      const virtualAtaAcs = { ...state.virtualDeviceDetails };
      if (action.meta?.arg?.dto?.portConfigOverrides) {
        state.virtualDeviceDetails = {
          ...(virtualAtaAcs as AcsAtaVirtualDeviceType),
          acsAtaPorts: (virtualAtaAcs as AcsAtaVirtualDeviceType).acsAtaPorts.map(port => {
            if (port.id === action.meta.arg.id) {
              return {
                ...port,
                portConfigOverrides: action.meta.arg.dto.portConfigOverrides,
              };
            }

            return port;
          }),
        };
      }
      state.virtualAtaAcsPatchStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(getFulfillmentOrderLogs.pending, state => {
      state.fulfillmentOrderLogsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getFulfillmentOrderLogs.fulfilled, (state, action) => {
      state.fulfillmentOrderLogs = action.payload;
      state.fulfillmentOrderLogsFetchStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(getFulfillmentOrderLogs.rejected, (state, action) => {
      state.fulfillmentOrderLogsFetchStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(getOrderLogs.pending, state => {
      state.orderLogsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getOrderLogs.fulfilled, (state, action) => {
      state.orderLogs = action.payload;
      state.orderLogsFetchStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(getOrderLogs.rejected, (state, action) => {
      state.orderLogsFetchStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    // Get Shipping Packages
    builder.addCase(getShippingPackages.pending, state => {
      state.shippingPackagesFetchStatus = FETCH_STATUS.PENDING;
      state.shippingPackagesError = null;
    });
    builder.addCase(getShippingPackages.fulfilled, (state, action) => {
      state.shippingPackagesFetchStatus = FETCH_STATUS.FULFILLED;
      state.shippingPackages = action.payload;
    });
    builder.addCase(getShippingPackages.rejected, (state, action) => {
      state.shippingPackagesFetchStatus = FETCH_STATUS.REJECTED;
      state.shippingPackagesError = action.payload as string;
    });

    // Get Shipping Package
    builder.addCase(getShippingPackage.pending, state => {
      state.currentShippingPackageFetchStatus = FETCH_STATUS.PENDING;
      state.currentShippingPackageError = null;
    });
    builder.addCase(getShippingPackage.fulfilled, (state, action) => {
      state.currentShippingPackageFetchStatus = FETCH_STATUS.FULFILLED;
      state.currentShippingPackage = action.payload;
    });
    builder.addCase(getShippingPackage.rejected, (state, action) => {
      state.currentShippingPackageFetchStatus = FETCH_STATUS.REJECTED;
      state.currentShippingPackageError = action.payload as string;
    });

    // Create Shipping Package
    builder.addCase(createShippingPackage.pending, state => {
      state.createShippingPackageStatus = FETCH_STATUS.PENDING;
      state.createShippingPackageError = null;
    });
    builder.addCase(createShippingPackage.fulfilled, (state, action) => {
      state.createShippingPackageStatus = FETCH_STATUS.FULFILLED;
      state.createShippingPackage = action.payload;
    });
    builder.addCase(createShippingPackage.rejected, (state, action) => {
      state.createShippingPackageStatus = FETCH_STATUS.REJECTED;
      state.createShippingPackageError = action.payload as string;
    });

    // Update Shipping Package
    builder.addCase(updateShippingPackage.pending, state => {
      state.updateShippingPackageStatus = FETCH_STATUS.PENDING;
      state.updateShippingPackageError = null;
    });
    builder.addCase(updateShippingPackage.fulfilled, (state, action) => {
      state.updateShippingPackageStatus = FETCH_STATUS.FULFILLED;
      state.shippingPackages = state.shippingPackages.map(pkg => (pkg.id === action.payload.id ? action.payload : pkg));
      if (state.currentShippingPackage?.id === action.payload.id) {
        state.currentShippingPackage = action.payload;
      }
    });
    builder.addCase(updateShippingPackage.rejected, (state, action) => {
      state.updateShippingPackageStatus = FETCH_STATUS.REJECTED;
      state.updateShippingPackageError = action.payload as string;
    });

    // Assign Devices to Package
    builder.addCase(assignDevicesToPackage.pending, state => {
      state.updateShippingPackageStatus = FETCH_STATUS.PENDING;
      state.updateShippingPackageError = null;
    });
    builder.addCase(assignDevicesToPackage.fulfilled, (state, action) => {
      state.updateShippingPackageStatus = FETCH_STATUS.FULFILLED;
      state.shippingPackages = state.shippingPackages.map(pkg => (pkg.id === action.payload.id ? action.payload : pkg));
      if (state.currentShippingPackage?.id === action.payload.id) {
        state.currentShippingPackage = action.payload;
      }
    });
    builder.addCase(assignDevicesToPackage.rejected, (state, action) => {
      state.updateShippingPackageStatus = FETCH_STATUS.REJECTED;
      state.updateShippingPackageError = action.payload as string;
    });

    // Assign Devices to Package
    builder.addCase(postImportDevicesToEquipmentGroup.pending, state => {
      state.importDevicesToEquipmentGroupPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postImportDevicesToEquipmentGroup.fulfilled, state => {
      state.importDevicesToEquipmentGroupPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device have been successfully imported to equipment group!'));
    });
    builder.addCase(postImportDevicesToEquipmentGroup.rejected, (state, action) => {
      state.importDevicesToEquipmentGroupPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    // Assign Devices to Package
    builder.addCase(patchImportDevicesToInventory.pending, state => {
      state.importDevicesToWarehousePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchImportDevicesToInventory.fulfilled, state => {
      state.importDevicesToWarehousePatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device have been successfully imported to warehouse!'));
    });
    builder.addCase(patchImportDevicesToInventory.rejected, (state, action) => {
      state.importDevicesToWarehousePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    // Replace Assigned Device
    builder.addCase(postReplaceInventoryDevice.pending, state => {
      state.replaceInventoryDevicePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postReplaceInventoryDevice.fulfilled, state => {
      state.replaceInventoryDevicePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device have been successfully replaced!'));
    });
    builder.addCase(postReplaceInventoryDevice.rejected, (state, action) => {
      state.replaceInventoryDevicePostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    builder.addCase(sendInventoryATACommand.pending, state => {
      state.sendInventoryATACommandPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(sendInventoryATACommand.fulfilled, state => {
      state.sendInventoryATACommandPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Command sent successfully!'));
    });
    builder.addCase(sendInventoryATACommand.rejected, (state, action) => {
      state.sendInventoryATACommandPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    builder.addCase(factoryResetInventoryATA.pending, state => {
      state.factoryResetInventoryATAPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(factoryResetInventoryATA.fulfilled, state => {
      state.factoryResetInventoryATAPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Command sent successfully!'));
    });
    builder.addCase(factoryResetInventoryATA.rejected, (state, action) => {
      state.factoryResetInventoryATAPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    // Delete Shipping Package
    builder.addCase(deleteShippingPackage.pending, state => {
      state.deleteShippingPackageStatus = FETCH_STATUS.PENDING;
      state.deleteShippingPackageError = null;
    });
    builder.addCase(deleteShippingPackage.fulfilled, (state, action) => {
      state.deleteShippingPackageStatus = FETCH_STATUS.FULFILLED;
      state.shippingPackages = state.shippingPackages.filter(pkg => pkg.id !== action.payload);
      if (state.currentShippingPackage?.id === action.payload) {
        state.currentShippingPackage = null;
      }
    });
    builder.addCase(deleteShippingPackage.rejected, (state, action) => {
      state.deleteShippingPackageStatus = FETCH_STATUS.REJECTED;
      state.deleteShippingPackageError = action.payload as string;
    });

    // PATCH UPDATE INVOICE STATUS
    builder.addCase(patchFulfillmentOrderUpdateInvoiceStatus.pending, state => {
      state.fulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchFulfillmentOrderUpdateInvoiceStatus.rejected, (state, action) => {
      state.fulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchFulfillmentOrderUpdateInvoiceStatus.fulfilled, state => {
      state.fulfillmentOrderDetailsUpdateInvoiceStatusPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Invoice created and sent to customer successfully!'));
    });

    builder.addCase(patchDeviceDetails.pending, state => {
      state.deviceDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchDeviceDetails.rejected, (state, action) => {
      state.deviceDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchDeviceDetails.fulfilled, state => {
      state.deviceDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device updated successfully!'));
    });

    builder.addCase(patchATAFirmware.pending, state => {
      state.deviceDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchATAFirmware.rejected, (state, action) => {
      state.deviceDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchATAFirmware.fulfilled, state => {
      state.deviceDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Device updated successfully!'));
    });

    // PATCH UPDATE PAYMENT STATUS
    builder.addCase(patchFulfillmentOrderUpdatePaymentStatus.pending, state => {
      state.fulfillmentOrderDetailsUpdatePaymentStatusPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchFulfillmentOrderUpdatePaymentStatus.rejected, (state, action) => {
      state.fulfillmentOrderDetailsUpdatePaymentStatusPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchFulfillmentOrderUpdatePaymentStatus.fulfilled, state => {
      state.fulfillmentOrderDetailsUpdatePaymentStatusPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Payment received and order approved to fulfill!'));
    });

    // ACCEPT FULFILLMENT ORDER
    builder.addCase(acceptFulfillmentOrder.pending, state => {
      state.fulfillmentOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(acceptFulfillmentOrder.rejected, (state, action) => {
      state.fulfillmentOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(acceptFulfillmentOrder.fulfilled, state => {
      state.fulfillmentOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Fulfillment order accepted successfully!'));
    });

    // UPDATE FULFILLMENT ORDER WAREHOUSE
    builder.addCase(updateFulfillmentOrderWarehouse.pending, state => {
      state.fulfillmentOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateFulfillmentOrderWarehouse.rejected, (state, action) => {
      state.fulfillmentOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(updateFulfillmentOrderWarehouse.fulfilled, state => {
      state.fulfillmentOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Warehouse updated successfully!'));
    });

    // CANCEL FULFILLMENT ORDER
    builder.addCase(cancelFulfillmentOrderDetails.pending, state => {
      state.fulfillmentOrderDetailsCancelStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(cancelFulfillmentOrderDetails.rejected, (state, action) => {
      state.fulfillmentOrderDetailsCancelStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(cancelFulfillmentOrderDetails.fulfilled, state => {
      state.fulfillmentOrderDetailsCancelStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Fulfillment order canceled successfully!'));
    });

    // DELETE FULFILLMENT ORDER ATTACHMENT
    builder.addCase(deleteFulfillmentOrderAttachment.pending, state => {
      state.fulfillmentOrderAttachmentDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteFulfillmentOrderAttachment.rejected, (state, action) => {
      state.fulfillmentOrderAttachmentDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(deleteFulfillmentOrderAttachment.fulfilled, state => {
      state.fulfillmentOrderAttachmentDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('File has been deleted!'));
    });

    // DELETE INVENTORY ORDER ATTACHMENT
    builder.addCase(deleteInventoryOrderAttachment.rejected, (state, action) => {
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(deleteInventoryOrderAttachment.fulfilled, () => {
      setTimeout(() => snackbarUtils.success('File has been deleted!'));
    });

    // DOWNLOAD INVENTORY ATTACHMENT
    builder.addCase(downloadInventoryAttachment.rejected, (_state, action) => {
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(downloadInventoryAttachment.fulfilled, () => {
      setTimeout(() => snackbarUtils.success('File download started!'));
    });

    // UPDATE BULK FIRMWARES
    builder.addCase(patchInventoryATAsFirmwares.pending, state => {
      state.patchInventoryATAsFirmwaresPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchInventoryATAsFirmwares.rejected, (state, action) => {
      state.patchInventoryATAsFirmwaresPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(patchInventoryATAsFirmwares.fulfilled, state => {
      state.patchInventoryATAsFirmwaresPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Devices firmware update started successfully!'));
    });

    builder.addCase(startDeviceQAcheck.rejected, (_, action) => {
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(startDeviceQAcheck.fulfilled, () => {
      setTimeout(() => snackbarUtils.success('The QA process has started!'));
    });

    builder.addCase(finishDeviceQAcheck.rejected, (_, action) => {
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
    builder.addCase(finishDeviceQAcheck.fulfilled, () => {
      setTimeout(() => snackbarUtils.success('QA process completed!'));
    });

    // TRANSFER BATTERY OWNERSHIP
    builder
      .addCase(postInventoryDeviceOwnershipTransfer.pending, state => {
        state.transferOwnershipPostStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postInventoryDeviceOwnershipTransfer.rejected, (state, action) => {
        state.transferOwnershipPostStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error((action.payload as string) ?? 'Failed to transfer device ownership'));
      })
      .addCase(postInventoryDeviceOwnershipTransfer.fulfilled, state => {
        state.transferOwnershipPostStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Device ownership transferred successfully!'));
      });
  },
});

export const {
  setAddATAToInventoryPostStatus,
  setAddDialerToInventoryPostStatus,
  setAddPowerCycleToInventoryPostStatus,
  setAddEnclosureToInventoryPostStatus,
  setAddRouterToInventoryPostStatus,
  setAddSIMToInventoryPostStatus,
  setAddSmartphoneToInventoryPostStatus,
  setAddBatteryToInventoryPostStatus,
  setInventoryListFetchStatus,
  setInventoryList,
  setDevicesListFetchStatus,
  setDevicesList,
  setDevicesRouteParams,
  setDeviceDetailsDeleteStatus,
  setFulfillmentOrdersListFetchStatus,
  setFulfillmentOrdersList,
  setFulfillmentOrderDetailsDeleteStatus,
  setFulfillmentOrderDetailsPostStatus,
  setFulfillmentOrderDetailsFetchStatus,
  setFulfillmentOrderDetails,
  setFulfillmentOrderDetailsApprovePatchStatus,
  setFulfillmentOrderDetailsAssignPatchStatus,
  setFulfillmentOrderDetailsConfirmDeliveryPatchStatus,
  setOrganizationModulesBundleFetchStatus,
  setOrganizationModulesBundle,
  setAssignDevicesToOrderVirtualDevicesPostStatus,
  setAcsAtaConfigurationsListFetchStatus,
  setAcsAtaPortConfigurationsList,
  setAcsAtaPortConfigurationsListFetchStatus,
  setAcsAtaConfigurationsList,
  setUnassignDevicesToOrderVirtualDevicesPostStatus,
  setVirtualDeviceDetailsFetchStatus,
  setVirtualDeviceDetails,
  setInventoryDeviceDetails,
  setInventoryDeviceDetailsFetchStatus,
  setVirtualAtaAcsPatchStatus,
  setVirtualAtaAcsPatchStatusPort,
  setVirtualDevicePostStatus,
  setVirtualDeviceDeleteStatus,
  setImportDevicesToEquipmentGroupPostStatus,
  setFulfillmentOrderDetailsCreated,
  setOrderCommentPostStatus,
  setDeviceDetailsPatchStatus,
  setMoveToInventoryPatchStatus,
  setImportDevicesToWarehousePatchStatus,
  setAddNonSerializedItemsToInventoryPostStatus,
  setAccessoryBundleSaveStatus,
  setNonSerializedItemsList,
  setNonSerializedItemsListFetchStatus,
  setTransferOwnershipPostStatus,
  setReplaceInventoryDevicePostStatus,
} = inventorySlice.actions;

export default inventorySlice.reducer;
